import { createTheme, ThemeOptions } from '@mui/material/styles'

import palette from './palette'

const buttonStyles = {
  MuiButton: {
    variants: [
      {
        props: { variant: 'primary' as const },
        style: {
          width: '100px',
          padding: '8px 16px',
          borderRadius: '4px',
          fontWeight: 600,
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'background-color 0.2s, opacity 0.2s',
          border: 'none',
          outline: 'none',
          backgroundColor: palette.black.main,
          color: palette.black.plus100,
          '&:hover': {
            backgroundColor: palette.black.plus10,
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: palette.black.plus70,
            color: palette.black.plus100,
          },
        },
      },
      {
        props: { variant: 'secondary' as const },
        style: {
          width: '100px',
          padding: '8px 16px',
          borderRadius: '4px',
          fontWeight: 600,
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'background-color 0.2s, opacity 0.2s',
          outline: 'none',
          backgroundColor: palette.black.plus100,
          color: palette.black.main,
          border: `2px solid ${palette.black.plus10}`,
          '&:hover': {
            backgroundColor: palette.black.plus90,
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: palette.black.plus80,
            color: palette.black.plus40,
          },
        },
      },
      {
        props: { variant: 'error' as const },
        style: {
          width: '100px',
          padding: '8px 16px',
          borderRadius: '4px',
          fontWeight: 600,
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'background-color 0.2s, opacity 0.2s',
          border: 'none',
          outline: 'none',
          backgroundColor: palette.red.main,
          color: palette.black.plus100,
          '&:hover': {
            backgroundColor: palette.red.plus5,
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: palette.red.plus10,
            color: palette.black.plus100,
          },
        },
      },
    ],
  },
}

const options: ThemeOptions = {
  typography: {
    fontFamily: ['Poppins', 'sans-serif'].join(','),
    allVariants: {
      fontWeight: 500,
      fontSize: 14,
      letterSpacing: '0.04em',
    },
  },
  palette,
  spacing: 8, // Define base spacing unit (8px)
  breakpoints: {
    unit: 'px',
    values: {
      mobile: 0,
      tablet: 769,
      laptop: 1024,
      desktop: 1216,
      widescreen: 1408,
    },
  },
  zIndex: {
    modal: 1300,
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 0.85,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            minHeight: 0,
          },
        },
      },
    },
    // Add the custom work ticket button styles
    ...buttonStyles,
  },
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false
    sm: false
    md: false
    lg: false
    xl: false
    mobile: true
    tablet: true
    laptop: true
    desktop: true
    widescreen: true
  }
  interface Palette {
    black: {
      main: string
      plus10: string
      plus20: string
      plus30: string
      plus40: string
      plus50: string
      plus60: string
      plus70: string
      plus80: string
      plus85: string
      plus90: string
      plus100: string
    }
    red: {
      minus5: string
      main: string
      plus5: string
      plus10: string
      plus40: string
      plus45: string
      plus80: string
    }
    supernova: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
    green: {
      minus5: string
      main: string
      plus5: string
      plus55: string
      lineRun: string
    }
    blue: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
    purple: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
  }

  interface PaletteOptions {
    black?: {
      main: string
      plus10: string
      plus20: string
      plus30: string
      plus40: string
      plus50: string
      plus60: string
      plus70: string
      plus80: string
      plus85: string
      plus90: string
      plus100: string
    }
    red?: {
      minus5: string
      main: string
      plus5: string
      plus10: string
      plus40: string
      plus45: string
      plus80: string
    }
    supernova?: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
    green?: {
      minus5: string
      main: string
      plus5: string
      plus55: string
      lineRun: string
    }
    blue?: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
    purple?: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
  }
}

// Add custom variants to Material UI
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true
    secondary: true
    error: true
  }
}

const theme = createTheme(options)

export default theme
